// The events screens' days and times (B3; D10, D32, D34): every one on the club's clock
// (Asia/Nicosia, CLUB_TIME_ZONE), whatever the phone's zone, read through toLocal; and said in the
// dictionary's words: the patterns and the days' and the months' names are codes in en.ts, like
// every other word a member reads (D6), so Turkish (Phase H) brings its own order and names.
import { addDays, isoWeekday, LocalDate, toLocal, WEEKS_AHEAD, type EventView } from '@grc/shared';
import { t, type Code } from '../../i18n';

const WEEKDAYS: readonly Code[] = [
  'date.monday',
  'date.tuesday',
  'date.wednesday',
  'date.thursday',
  'date.friday',
  'date.saturday',
  'date.sunday',
];

/** The month view's columns, Monday first (D10's weeks): the short name, and the full one. */
export const WEEK_COLUMNS: readonly { short: Code; long: Code }[] = [
  { short: 'date.mon', long: 'date.monday' },
  { short: 'date.tue', long: 'date.tuesday' },
  { short: 'date.wed', long: 'date.wednesday' },
  { short: 'date.thu', long: 'date.thursday' },
  { short: 'date.fri', long: 'date.friday' },
  { short: 'date.sat', long: 'date.saturday' },
  { short: 'date.sun', long: 'date.sunday' },
];

const MONTHS: readonly Code[] = [
  'date.january',
  'date.february',
  'date.march',
  'date.april',
  'date.may',
  'date.june',
  'date.july',
  'date.august',
  'date.september',
  'date.october',
  'date.november',
  'date.december',
];

function codeAt(codes: readonly Code[], index: number): Code {
  const code = codes[index];
  if (code === undefined) throw new RangeError(`no name for ${index}`);
  return code;
}

/** A date's year, month and day as numbers: `2026-10-20` is 2026, 10, 20. */
function parts(date: string): { year: number; month: number; day: number } {
  const [year = NaN, month = NaN, day = NaN] = date.split('-').map(Number);
  return { year, month, day };
}

/** The club's date at an instant, `2026-10-20`. */
export function clubDate(instant: number | string): string {
  return toLocal(instant).date;
}

/** When an event starts on the club's clock, `19:00`. */
export function startTime(event: Pick<EventView, 'startsAt'>): string {
  return toLocal(event.startsAt).time;
}

/** When an event is over, in ms: its duration run from its start (D33). */
export function endsAt(event: Pick<EventView, 'startsAt' | 'durationMin'>): number {
  return Date.parse(event.startsAt) + event.durationMin * 60_000;
}

/** "Tuesday 20 October"; with its year ("Friday 1 January 2027") when it's not today's year. */
export function longDate(date: string, today: string): string {
  const { year, month, day } = parts(date);
  const vars = {
    weekday: t(codeAt(WEEKDAYS, isoWeekday(date) - 1)),
    day,
    month: t(codeAt(MONTHS, month - 1)),
    year,
  };
  return year === parts(today).year ? t('date.long', vars) : t('date.longYear', vars);
}

/** A day's heading: today, tomorrow and yesterday named before the date (the owner's words). */
export function dayHeading(date: string, today: string): string {
  const long = longDate(date, today);
  if (date === today) return t('date.today', { date: long });
  if (date === addDays(today, 1)) return t('date.tomorrow', { date: long });
  if (date === addDays(today, -1)) return t('date.yesterday', { date: long });
  return long;
}

/** When an event runs on the club's clock: "19:00–20:30", or "22:00–02:00 the next day". */
export function timeRange(event: Pick<EventView, 'startsAt' | 'durationMin'>): string {
  const start = toLocal(event.startsAt);
  const end = toLocal(endsAt(event));
  // An event lasts a day at most (DurationMin), so it ends on its own date or the next. Only one
  // of over 23 hours, begun in the last hour before March's 23-hour day, ends a date later still,
  // which these words call the next day too.
  const code = end.date === start.date ? 'date.timeRange' : 'date.timeRangeNextDay';
  return t(code, { start: start.time, end: end.time });
}

/** A day of a list: its date on the club's clock, and its events in time order. */
export type EventDay = { date: string; events: EventView[] };

function compare(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Two events in time order: by their start, then by their id, as the API orders them. */
export function byStart(a: EventView, b: EventView): number {
  return Date.parse(a.startsAt) - Date.parse(b.startsAt) || compare(a.id, b.id);
}

/**
 * The events by their day on the club's clock, the days soonest or newest first, each day's events
 * in time order (the owner's answer for the past list, D34).
 */
export function byDay(events: readonly EventView[], order: 'soonest' | 'newest'): EventDay[] {
  const days = new Map<string, EventView[]>();
  for (const event of events) {
    const date = clubDate(event.startsAt);
    const day = days.get(date);
    if (day) day.push(event);
    else days.set(date, [event]);
  }
  const list = [...days].map(([date, dayEvents]) => ({ date, events: dayEvents.sort(byStart) }));
  return list.sort((a, b) =>
    order === 'soonest' ? compare(a.date, b.date) : compare(b.date, a.date),
  );
}

/** A date's month, `2026-10`. */
export function monthOf(date: string): string {
  return date.slice(0, 7);
}

/** A month as a link carries it (`2026-10`), or undefined for anything else. */
export function readMonth(text: string | null): string | undefined {
  return text !== null && /^\d{4}-(0[1-9]|1[0-2])$/.test(text) ? text : undefined;
}

/** A date of `month` as a link carries it, or undefined for anything else. */
export function readDay(text: string | null, month: string): string | undefined {
  return text !== null && LocalDate.safeParse(text).success && monthOf(text) === month
    ? text
    : undefined;
}

/** The month `n` months after `month` (before it, when negative). */
export function addMonths(month: string, n: number): string {
  const { year, month: m } = parts(`${month}-01`);
  const index = year * 12 + (m - 1) + n;
  const y = Math.floor(index / 12);
  return `${String(y).padStart(4, '0')}-${String(index - y * 12 + 1).padStart(2, '0')}`;
}

/** "October 2026". */
export function monthTitle(month: string): string {
  const { year, month: m } = parts(`${month}-01`);
  return t('date.monthYear', { month: t(codeAt(MONTHS, m - 1)), year });
}

/**
 * The month's weeks, Monday to Sunday (D10): seven dates each, null for the days of the months
 * either side.
 */
export function monthWeeks(month: string): (string | null)[][] {
  const first = `${month}-01`;
  const days: (string | null)[] = Array.from({ length: isoWeekday(first) - 1 }, () => null);
  for (let date = first; monthOf(date) === month; date = addDays(date, 1)) days.push(date);
  while (days.length % 7 !== 0) days.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

/** A month's last date. */
export function lastDateOf(month: string): string {
  return addDays(`${addMonths(month, 1)}-01`, -1);
}

/**
 * The last date a weekly series' events exist for, seen from the club's today: the same weekday
 * eight weeks on (D12; the API's generationWindow). None is made beyond it yet.
 */
export function lastMadeDate(today: string): string {
  return addDays(today, 7 * WEEKS_AHEAD);
}
